/**
 * Your portfolios and reports, on a phone, signed in (ROADMAP "The phone
 * branch" (i), owner pick 5a, 2026-09-29).
 *
 * Read here, create on a desktop: saved portfolios, then risk reports and
 * rebalance comparisons, newest first, each opening its phone report page.
 * Creating a portfolio or running an analysis is one row that hands over.
 *
 * The same three sources the desktop hub reads (`savedPortfolioStore`,
 * `riskReportStore`, `listRebalanceReports`) — all list reads, none billed.
 *
 * Deviation from the 5a frame, on purpose: a risk report row shows its risk
 * score and holdings, not "what the worst crash would cost". The saved SUMMARY
 * carries no stress figures and no currency, and showing one would mean either
 * inventing it or loading every full report to draw a list.
 */
import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Chip, Skeleton, Typography } from '@mui/material';
import { ChevronRight, DesktopWindows } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useSavedPortfolioStore } from '../stores/savedPortfolioStore';
import { useRiskReportStore } from '../stores/riskReportStore';
import portfolioService, { type RebalanceReportSummary } from '../services/portfolioService';
import { pct } from '../components/research/RebalanceVerdictTable';
import ShareLinkButton from './ShareLinkButton';

const shortDate = (d: Date | string | undefined) => {
  if (!d) return '';
  const date = d instanceof Date ? d : new Date(d);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    component="h2"
    sx={{ px: 2, pt: 2.5, pb: 1, fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'text.secondary' }}
  >
    {children}
  </Typography>
);

const rowSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  minHeight: 56,
  px: 2,
  py: 1.25,
  bgcolor: 'background.paper',
  borderTop: '1px solid',
  borderColor: 'divider',
  color: 'text.primary',
  textDecoration: 'none',
} as const;

const PortfoliosPhone: React.FC = () => {
  const { user } = useAuth();
  const { portfolios, isLoading: portfoliosLoading, loadPortfolios } = useSavedPortfolioStore();
  const { reports, isLoading: isLoadingReports, loadReports } = useRiskReportStore();
  const [rebalances, setRebalances] = useState<RebalanceReportSummary[] | null>(null);

  useEffect(() => {
    if (!user?.uid) return;
    loadPortfolios(user.uid);
    loadReports(user.uid);
    let cancelled = false;
    (async () => {
      try {
        const res = await portfolioService.listRebalanceReports(await user.getIdToken());
        if (!cancelled) setRebalances(res.success ? res.reports || [] : []);
      } catch {
        if (!cancelled) setRebalances([]);
      }
    })();
    return () => { cancelled = true; };
    // `user?.uid`, not `user`: the context object is a new identity every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  const loading = portfoliosLoading || isLoadingReports || rebalances === null;
  const empty = !loading && portfolios.length === 0 && reports.length === 0 && (rebalances?.length ?? 0) === 0;

  if (loading && portfolios.length === 0 && reports.length === 0) {
    return (
      <Box sx={{ p: 2 }} aria-busy="true" aria-label="Loading your portfolios">
        <Skeleton width="40%" />
        {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} variant="rounded" height={56} sx={{ mt: 1.25 }} />)}
      </Box>
    );
  }

  if (empty) {
    return (
      <Box sx={{ px: 2.5, pt: 5, pb: 3 }}>
        <Typography component="h1" sx={{ fontWeight: 700, fontSize: '1.4rem', mb: 1 }}>
          No saved portfolios yet
        </Typography>
        <Typography sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.6 }}>
          Add your holdings on a laptop and run your first analysis. Your portfolios and reports will be here to read on your phone.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <ShareLinkButton source="portfolios_phone_empty" variant="outlined" />
        </Box>
      </Box>
    );
  }

  const byNewest = <T extends { createdAt: Date | string }>(xs: T[]) =>
    [...xs].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  return (
    <Box sx={{ pb: 3 }}>
      <Typography component="h1" sx={{ px: 2, pt: 2.5, fontWeight: 700, fontSize: '1.4rem' }}>
        Your portfolios
      </Typography>

      {portfolios.length > 0 && <SectionLabel>Saved portfolios</SectionLabel>}
      {byNewest(portfolios).map((p) => {
        const latest = byNewest(reports).find((r) => r.portfolioName === p.name);
        const content = (
          <>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 500 }} noWrap>{p.name}</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                <Chip size="small" variant="outlined" label={`${p.holdings.length} holdings`} />
                {p.automated && <Chip size="small" variant="outlined" label="Signals checked daily" />}
                {p.origin?.kind === 'simulation' && <Chip size="small" variant="outlined" label="From a simulation" />}
              </Box>
            </Box>
            {latest ? <ChevronRight sx={{ color: 'text.disabled' }} /> : null}
          </>
        );
        // A portfolio opens its latest report; one never analysed has nothing to read here.
        return latest ? (
          <Box key={p.id} component={RouterLink} to={`/portfolio/risk-results/${latest.id}`} sx={rowSx}>{content}</Box>
        ) : (
          <Box key={p.id} sx={rowSx}>{content}</Box>
        );
      })}

      {reports.length > 0 && <SectionLabel>Risk reports</SectionLabel>}
      {byNewest(reports).map((r) => (
        <Box key={r.id} component={RouterLink} to={`/portfolio/risk-results/${r.id}`} sx={rowSx}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 500 }} noWrap>{r.portfolioName}</Typography>
            <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
              {shortDate(r.createdAt)} · {r.holdingsCount} holdings
            </Typography>
          </Box>
          {typeof r.riskScore === 'number' && (
            <Box sx={{ textAlign: 'right' }}>
              <Typography sx={{ fontWeight: 700 }}>{r.riskScore.toFixed(1)}</Typography>
              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>risk / 10</Typography>
            </Box>
          )}
          <ChevronRight sx={{ color: 'text.disabled' }} />
        </Box>
      ))}

      {(rebalances?.length ?? 0) > 0 && <SectionLabel>Rebalance comparisons</SectionLabel>}
      {byNewest(rebalances || []).map((r) => (
        <Box key={r.id} component={RouterLink} to={`/portfolio/rebalance/${r.id}`} sx={rowSx}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 500 }} noWrap>
              {r.summary.optionCount ? `${r.summary.optionCount} mixes compared` : 'Rebalance comparison'}
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }} noWrap>
              {shortDate(r.createdAt)}
              {r.summary.topLabel ? ` · Steadiest: ${r.summary.topLabel}` : ''}
              {typeof r.summary.topTailDrawdown === 'number' ? ` · bad case −${pct(r.summary.topTailDrawdown)}` : ''}
            </Typography>
          </Box>
          <ChevronRight sx={{ color: 'text.disabled' }} />
        </Box>
      ))}

      <Box component={RouterLink} to="/portfolio/risk-assessment/new" sx={{ ...rowSx, mt: 2, color: 'text.secondary' }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 500, color: 'text.primary' }}>New portfolio or new analysis</Typography>
          <Typography sx={{ fontSize: '0.8rem' }}>Add holdings and run it on a desktop</Typography>
        </Box>
        <DesktopWindows sx={{ color: 'text.disabled' }} />
      </Box>
    </Box>
  );
};

export default PortfoliosPhone;
